/**
 * @fileoverview Security IO — the app's security node (control-plane only).
 * @module apps/network/global/io/nodes/security/security-io
 *
 * THE single point of exit for `@liquidefy/network-security` in
 * apps/network — mirrors deployment-io.ts (principle 1: only this file
 * imports the binding; every lens reads through the node).
 *
 * THE TWO-PLANE WALL: the app holds ONLY the browser-safe CONTROL plane
 * (`createNetworkSecurityControl` — 10 pure lenses; no CryptoHandle, no
 * secrets, no `node:*`). The EXECUTION plane (audit chain, vault, signing,
 * the authoritative authorize+audit) lives in the deployment server. The
 * app reaches the floor's READ side via the deployment command port
 * (`security.*` ops) and projects the server's `security.*` stream events.
 * It MUST NOT import `@liquidefy/network-security/execution`.
 *
 * Three lenses mount over this node:
 *   • ValidationIO — `control.validation`: instant client-side input
 *     sanitization (defense-in-depth; the server re-validates as authority).
 *   • GuardIO — `control.permissions`: deny-by-default RBAC PRE-CHECK for
 *     UX (disable/hide forbidden actions); the server floor is the authority.
 *   • AuditTrailIO — projects the server's `security.*` stream + the
 *     `security.audit.verify` badge (mounts over the deployment node).
 *
 * Pure TS: zero DOM, zero Svelte. The Svelte-bound hooks live in the
 * `*IO.svelte` portals and re-export these contracts (one-contract join).
 */

import {
  createNetworkSecurityControl,
  type NetworkSecurityControl,
} from "@liquidefy/network-security";
import type { DeploymentStreamEvent } from "../deployment/deployment-io.js";

// ─── Role + action vocabulary ─────────────────────────────────────

export type RoleName = "viewer" | "deployer" | "operator" | "auditor" | "owner";
export const ROLE_NAMES: readonly RoleName[] = ["viewer", "deployer", "operator", "auditor", "owner"];

/** The engine's 7-value SecurityAction vocabulary, mirrored locally so the
 *  app needn't import the engine's types directly. Assignable to the
 *  engine's `SecurityAction` (identical literal union). */
export type Action = "read" | "write" | "execute" | "delete" | "create" | "deploy" | "admin";

export interface OpClassification {
  readonly resourceType: string;
  readonly action: Action;
}

// ─── ⚠️ CLIENT-SIDE UX MIRROR of the deployment server's grant matrix ──
// CANONICAL SOURCE: network/servers/deployment/0.1.0/src/security/roles.ts.
// This is a deliberately-compact mirror used ONLY to disable/hide actions
// the principal can't perform (instant UX). The server's floor ALWAYS
// re-checks deny-by-default and is the sole authority. Keep in sync — a
// future refactor should share ONE definition (see the spawned task).

const READABLE_RTS = [
  "projects", "release", "rollback", "db", "storage", "cache", "queue",
  "dns", "scheduling", "telemetry", "creds", "registrar", "billing",
  "traffic", "gate", "tokens",
] as const;
const DEPLOYER_RTS = [
  "projects", "release", "db", "storage", "dns", "cache", "queue",
  "creds", "scheduling", "telemetry",
] as const;
const PLATFORM_RTS = [
  "dns-admin", "traffic", "gate", "rollback", "daemon", "enforcement", "tokens",
] as const;
const SECURITY_RTS = [
  "audit", "attestation", "incident", "debt", "compliance",
  "gate-verdict", "topology-integrity", "security",
] as const;

const READ_ONLY: readonly Action[] = ["read"];
const DEPLOYER_ACTIONS: readonly Action[] = ["read", "create", "write", "deploy", "execute"];
const ALL_ACTIONS: readonly Action[] = ["read", "write", "execute", "delete", "create", "deploy", "admin"];

interface Grant { readonly resourceType: string; readonly actions: readonly Action[]; }

const ROLE_GRANTS: Record<RoleName, readonly Grant[]> = {
  viewer: [
    ...READABLE_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
    ...SECURITY_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
  ],
  deployer: [
    ...READABLE_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
    ...DEPLOYER_RTS.map((rt) => ({ resourceType: rt, actions: DEPLOYER_ACTIONS })),
    ...SECURITY_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
  ],
  operator: [
    ...READABLE_RTS.map((rt) => ({ resourceType: rt, actions: ALL_ACTIONS })),
    ...PLATFORM_RTS.map((rt) => ({ resourceType: rt, actions: ALL_ACTIONS })),
    ...SECURITY_RTS.map((rt) => ({ resourceType: rt, actions: ALL_ACTIONS })),
  ],
  auditor: [
    ...READABLE_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
    ...SECURITY_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
  ],
  owner: [
    ...READABLE_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
    ...DEPLOYER_RTS.map((rt) => ({ resourceType: rt, actions: ALL_ACTIONS })),
    ...SECURITY_RTS.map((rt) => ({ resourceType: rt, actions: READ_ONLY })),
  ],
};

type SecurityLevel = "public" | "internal" | "confidential" | "restricted" | "top-secret";
const ROLE_SENSITIVITY: Record<RoleName, SecurityLevel> = {
  viewer: "internal", deployer: "internal", operator: "restricted",
  auditor: "confidential", owner: "restricted",
};

function collapse(grants: readonly Grant[]): Grant[] {
  const byRt = new Map<string, Set<Action>>();
  for (const g of grants) {
    let set = byRt.get(g.resourceType);
    if (!set) { set = new Set(); byRt.set(g.resourceType, set); }
    for (const a of g.actions) set.add(a);
  }
  return [...byRt].map(([resourceType, set]) => ({ resourceType, actions: [...set] }));
}

// ─── Op classification (mirrors the server's classifyOp) ──────────

const ARC_PREFIXES: readonly (readonly [string, string])[] = [
  ["security.audit.", "audit"], ["security.", "security"], ["dns.admin.", "dns-admin"],
  ["projects.", "projects"], ["release.", "release"], ["rollback.", "rollback"],
  ["db.", "db"], ["storage.", "storage"], ["cache.", "cache"], ["queue.", "queue"],
  ["dns.", "dns"], ["scheduling.", "scheduling"], ["telemetry.", "telemetry"],
  ["creds.", "creds"], ["credentials.", "creds"], ["registrar.", "registrar"],
  ["billing.", "billing"], ["traffic.", "traffic"], ["gate.", "gate"],
  ["tokens.", "tokens"], ["daemon.", "daemon"], ["enforcement.", "enforcement"],
];

const READ_OPS = new Set<string>([
  "projects.list", "projects.get", "release.list", "release.get", "rollback.list",
  "telemetry.usage", "telemetry.events", "telemetry.config", "db.list", "storage.list",
  "cache.list", "queue.list", "billing.status", "billing.invoices", "billing.usage",
  "billing.tiers", "dns.zones.list", "dns.records.list", "registrar.search",
  "scheduling.cron.list",
]);

function resourceTypeFor(op: string): string {
  for (const [prefix, rt] of ARC_PREFIXES) if (op.startsWith(prefix)) return rt;
  const head = op.split(".")[0];
  return head && head.length > 0 ? head : "unknown";
}
function arcPrefixFor(op: string): string {
  for (const [prefix] of ARC_PREFIXES) if (op.startsWith(prefix)) return prefix;
  const i = op.indexOf(".");
  return i >= 0 ? op.slice(0, i + 1) : "";
}
function actionFor(op: string): Action {
  if (READ_OPS.has(op)) return "read";
  const prefix = arcPrefixFor(op);
  const verb = (prefix ? op.slice(prefix.length) : op).toLowerCase();
  const has = (...w: string[]): boolean => w.some((x) => verb.includes(x));
  if (has("override", "setmode", "mode", "danger", "force", "freeze", "grant", "admin")) return "admin";
  if (has("teardown", "destroy", "delete", "remove", "drop", "purge", "trash", "revoke", "abort", "cancel")) return "delete";
  if (has("run", "deploy", "release", "cutover", "promote", "shift", "bind", "ship")) return "deploy";
  if (has("provision", "create", "new", "register", "issue", "mint", "foldercreate")) return "create";
  if (has("flush", "drain", "invalidate", "warm", "restart", "pause", "resume", "exec", "invoke",
          "trigger", "snapshot", "restore", "migrate", "seed", "renew", "apply", "await", "peek", "publish")) return "execute";
  if (has("list", "get", "status", "describe", "tail", "history", "verify", "inspect",
          "plan", "preview", "summary", "usage", "view", "check", "events", "invoices",
          "tiers", "zones", "objects", "search", "connect", "whoami", "scan", "detect")) return "read";
  if (has("set", "update", "upsert", "rotate", "configure", "edit", "rename", "attach", "enable", "disable", "scale")) return "write";
  return "admin";
}
export function classifyOp(op: string): OpClassification {
  return { resourceType: resourceTypeFor(op), action: actionFor(op) };
}

/** Approximate the principal's role from the WS hello-frame session (UX
 *  default). The server's `security.whoami` returns the authoritative
 *  role — call it to refine. JWT (dashboard) → deployer; tokens default
 *  to viewer (scopes aren't in the hello frame). */
export function principalRoleFromHello(hello: { readonly kind?: "jwt" | "token" } | null | undefined): RoleName {
  return hello?.kind === "jwt" ? "deployer" : "viewer";
}

// ─── The control node (holds the one control instance + seeded roles) ──

export interface SecurityControlNode {
  readonly control: NetworkSecurityControl;
  roleId(name: RoleName): string | undefined;
  /** Deny-by-default RBAC pre-check via the engine's permissions lens. */
  can(role: RoleName, op: string): boolean;
  classify(op: string): OpClassification;
  destroy(): void;
}

export function createSecurityControlNode(): SecurityControlNode {
  const control = createNetworkSecurityControl();
  const ids = new Map<RoleName, string>();

  for (const name of ROLE_NAMES) {
    const role = control.permissions.createRole(name, ROLE_SENSITIVITY[name]);
    ids.set(name, role.id);
    for (const g of collapse(ROLE_GRANTS[name])) {
      control.permissions.addPermission(role.id, {
        id: `${name}:${g.resourceType}`,
        name: `${name} ${g.resourceType}`,
        resourceType: g.resourceType,
        actions: g.actions,
        constraints: [],
      });
    }
  }

  return {
    control,
    roleId: (n) => ids.get(n),
    classify: classifyOp,
    can(role, op) {
      const id = ids.get(role);
      if (!id) return false;
      const { resourceType, action } = classifyOp(op);
      const res = control.permissions.checkAccess(id, { type: resourceType, id: op }, action);
      return res.success === true && res.value === true;
    },
    destroy() { /* the control plane is pure — nothing to release in Phase 1 */ },
  };
}

// ─── Context contracts (single-sourced; portals re-export, never re-declare) ──

export const SECURITY_IO_KEY = "security-io" as const;
export interface SecurityIOContext {
  readonly ready: boolean;
  readonly control: NetworkSecurityControl | null;
  /** The principal's role (UX pre-check basis; server is authoritative). */
  readonly role: RoleName;
  /** Set the principal's role (e.g. from `security.whoami`). */
  setRole(role: RoleName): void;
  /** Deny-by-default RBAC pre-check for an op (UX only). */
  can(op: string): boolean;
  classify(op: string): OpClassification;
}

export const VALIDATION_IO_KEY = "security-validation-io" as const;
export interface ValidationCheck {
  readonly ok: boolean;
  readonly sanitized: string;
}
export interface ValidationIOContext {
  readonly ready: boolean;
  sanitizeHtml(input: string): string;
  sanitizeSql(input: string): string;
  sanitizePath(input: string): string;
  /** True iff `value` is within `maxDepth` (default 12). */
  validateStructure(value: unknown, maxDepth?: number): boolean;
  /** Sanitize + report whether the input was already clean. */
  check(input: string, kind: "html" | "sql" | "path"): ValidationCheck;
}

export const GUARD_IO_KEY = "security-guard-io" as const;
export interface GuardIOContext {
  readonly role: RoleName;
  /** Deny-by-default pre-check (UX only; server authoritative). */
  can(op: string): boolean;
  /** A short reason when an op is NOT permitted (for tooltips), else null. */
  denyReason(op: string): string | null;
  classify(op: string): OpClassification;
}

export const AUDIT_TRAIL_IO_KEY = "security-audit-trail-io" as const;
export interface AuditTrailEntry {
  readonly atMs: number;
  readonly op: string;
  /** security.audit | security.would-deny | security.denied | security.validation-blocked */
  readonly code: string;
  readonly status: "success" | "failure" | "unknown";
  readonly role?: string;
  readonly resourceType?: string;
  readonly action?: string;
  readonly reason?: string;
  readonly allowed?: boolean;
  readonly wouldDeny?: boolean;
  readonly persisted?: boolean;
  readonly durableSeq?: number;
}
export type VerifyBadge = "unknown" | "verifying" | "intact" | "tampered" | "error";
export interface AuditTrailIOContext {
  /** Live `security.*` events projected from the deployment stream (newest last). */
  readonly entries: readonly AuditTrailEntry[];
  /** The subset needing attention: denials, would-denies, validation blocks. */
  readonly attention: readonly AuditTrailEntry[];
  readonly latest: AuditTrailEntry | null;
  readonly count: number;
  /** The tamper-evidence badge (set by `verify()`). */
  readonly badge: VerifyBadge;
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Dispatch `security.audit.verify` → updates `badge`, returns chain-intact. */
  verify(): Promise<boolean>;
  /** Dispatch `security.audit.tail` → backfill entries that predate the WS. */
  backfill(limit?: number): Promise<void>;
}

/** Parse a deployment `security.*` stream event into an AuditTrailEntry. */
export function toAuditEntry(e: DeploymentStreamEvent): AuditTrailEntry {
  const d = (typeof e.detail === "object" && e.detail !== null ? e.detail : {}) as Record<string, unknown>;
  const status = d["status"] === "success" || d["status"] === "failure" ? d["status"] : "unknown";
  const base: AuditTrailEntry = {
    atMs: e.atMs,
    op: e.op,
    code: e.code,
    status,
    ...(typeof d["role"] === "string" ? { role: d["role"] } : {}),
    ...(typeof d["resourceType"] === "string" ? { resourceType: d["resourceType"] } : {}),
    ...(typeof d["action"] === "string" ? { action: d["action"] } : {}),
    ...(typeof d["reason"] === "string" ? { reason: d["reason"] } : {}),
    ...(typeof d["allowed"] === "boolean" ? { allowed: d["allowed"] } : {}),
    ...(typeof d["wouldDeny"] === "boolean" ? { wouldDeny: d["wouldDeny"] } : {}),
    ...(typeof d["persisted"] === "boolean" ? { persisted: d["persisted"] } : {}),
    ...(typeof d["durableSeq"] === "number" ? { durableSeq: d["durableSeq"] } : {}),
  };
  return base;
}

/** True iff an entry is a denial / would-deny / validation block. */
export function needsAttention(e: AuditTrailEntry): boolean {
  return e.wouldDeny === true || e.code === "security.denied" || e.code === "security.validation-blocked";
}

// ─── VaultStatusIO (Phase 2) — the secrets-vault status view ──────

export const VAULT_STATUS_IO_KEY = "security-vault-status-io" as const;

export interface VaultStatusSnapshot {
  readonly available: boolean;
  /** The KEK source mode ("passphrase" | "kms" | ...). */
  readonly kekMode: string;
  readonly sealed: boolean;
  readonly secretCount: number;
  /** True iff the vault was restored from a persisted export this boot. */
  readonly restored: boolean;
}

export interface VaultStatusIOContext {
  readonly status: VaultStatusSnapshot | null;
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Dispatch `security.vault.status` → refresh + return the snapshot. */
  refresh(): Promise<VaultStatusSnapshot | null>;
}

/** Parse a `security.vault.status` dispatch result into a snapshot. */
export function toVaultStatus(raw: unknown): VaultStatusSnapshot | null {
  if (typeof raw !== "object" || raw === null) return null;
  const x = raw as Record<string, unknown>;
  return {
    available: x["available"] === true,
    kekMode: typeof x["kekMode"] === "string" ? x["kekMode"] : "unknown",
    sealed: x["sealed"] === true,
    secretCount: typeof x["secretCount"] === "number" ? x["secretCount"] : 0,
    restored: x["restored"] === true,
  };
}

// ─── AttestationIO (Phase 2 scaffolding) — per-artifact provenance ─
// The view structure + stream projection are wired now; the deployment
// server starts emitting `security.attestation.*` events in Phase 3
// (deploy integrity), at which point this populates with zero changes here.

export const ATTESTATION_IO_KEY = "security-attestation-io" as const;

export interface AttestationEntry {
  readonly atMs: number;
  readonly op: string;
  readonly code: string;
  readonly status: "success" | "failure" | "unknown";
  readonly artifactPath?: string;
  readonly attestationId?: string;
  readonly verified?: boolean;
}

export interface AttestationIOContext {
  /** Provenance/attestation events projected from the deployment stream.
   *  Empty scaffold until Phase 3 emits `security.attestation.*`. */
  readonly attestations: readonly AttestationEntry[];
  readonly latest: AttestationEntry | null;
  readonly count: number;
}

/** True iff a stream event is a security attestation/provenance event. */
export function isAttestationEvent(e: DeploymentStreamEvent): boolean {
  return e.origin === "security" && e.code.startsWith("security.attestation");
}

/** Parse a security attestation stream event into an AttestationEntry. */
export function toAttestationEntry(e: DeploymentStreamEvent): AttestationEntry {
  const d = (typeof e.detail === "object" && e.detail !== null ? e.detail : {}) as Record<string, unknown>;
  const status = d["status"] === "success" || d["status"] === "failure" ? d["status"] : "unknown";
  return {
    atMs: e.atMs,
    op: e.op,
    code: e.code,
    status,
    ...(typeof d["artifactPath"] === "string" ? { artifactPath: d["artifactPath"] } : {}),
    ...(typeof d["attestationId"] === "string" ? { attestationId: d["attestationId"] } : {}),
    ...(typeof d["verified"] === "boolean" ? { verified: d["verified"] } : {}),
  };
}

// ─── GateVerdictIO (Phase 3) — the 5 deploy gates as security verdicts ─
// Projects gate.* stream events (budget/freeze/health/attestation/approval/
// capacity). The attestation gate carries security's provenance verdict.

export const GATE_VERDICT_IO_KEY = "security-gate-verdict-io" as const;

export type GateVerdict = "pass" | "deny" | "wait" | "unknown";

export interface GateVerdictEntry {
  readonly atMs: number;
  readonly dim: string;
  readonly verdict: GateVerdict;
  readonly op: string;
  readonly reason?: string;
  /** attestation gate: whether the security floor attested the artifact. */
  readonly securityAttested?: boolean;
}

export interface GateVerdictIOContext {
  /** All gate verdict events (newest last). */
  readonly verdicts: readonly GateVerdictEntry[];
  /** Latest verdict per dimension. */
  readonly byDimension: Readonly<Record<string, GateVerdictEntry>>;
  /** Dimensions currently denying. */
  readonly denials: readonly GateVerdictEntry[];
  readonly latest: GateVerdictEntry | null;
}

/** True iff a stream event is a deploy-gate verdict. */
export function isGateEvent(e: DeploymentStreamEvent): boolean {
  return e.op.startsWith("gate.");
}

/** Parse a gate.* stream event into a GateVerdictEntry. */
export function toGateVerdict(e: DeploymentStreamEvent): GateVerdictEntry {
  const d = (typeof e.detail === "object" && e.detail !== null ? e.detail : {}) as Record<string, unknown>;
  const dim = typeof d["dim"] === "string" ? d["dim"] : e.op.replace(/^gate\./, "");
  const verdict: GateVerdict =
    d["verdict"] === "pass" || d["verdict"] === "deny" || d["verdict"] === "wait"
      ? d["verdict"]
      : e.code === "OK" ? "pass" : e.code === "WAIT" ? "wait" : e.code === "DENY" ? "deny" : "unknown";
  return {
    atMs: e.atMs,
    dim,
    verdict,
    op: e.op,
    ...(typeof d["reason"] === "string" ? { reason: d["reason"] } : {}),
    ...(typeof d["securityAttested"] === "boolean" ? { securityAttested: d["securityAttested"] } : {}),
  };
}

// ─── ThreatIO / DebtIO / IncidentIO (Phase 4 — detect) ────────────

export interface ScanReport {
  readonly label: string;
  readonly findingsCount: number;
  readonly riskScore: number;
  readonly debtScore: number;
  readonly debtEntryCount: number;
}

export const THREAT_IO_KEY = "security-threat-io" as const;
export interface ThreatEntry {
  readonly atMs: number;
  readonly label: string;
  readonly findingsCount: number;
  readonly riskScore: number;
  readonly status: "success" | "failure";
}
export interface ThreatIOContext {
  /** Scan results projected from security.scan stream events (newest last). */
  readonly scans: readonly ThreatEntry[];
  readonly latest: ThreatEntry | null;
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Run a vulnerability scan (dispatch security.scan). */
  scan(content: string, label?: string): Promise<ScanReport | null>;
}

export const DEBT_IO_KEY = "security-debt-io" as const;
export interface DebtSummary {
  readonly totalScore: number;
  readonly entryCount: number;
}
export interface DebtIOContext {
  readonly summary: DebtSummary | null;
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Refresh the governance debt summary (dispatch security.debt.summary). */
  refresh(): Promise<DebtSummary | null>;
}

export const INCIDENT_IO_KEY = "security-incident-io" as const;
export interface IncidentEntry {
  readonly id: string;
  readonly title: string;
  readonly type: string;
  readonly severity: string;
  readonly status: string;
  readonly detectedAt: number;
}
export interface IncidentIOContext {
  readonly incidents: readonly IncidentEntry[];
  readonly count: number;
  readonly inFlight: boolean;
  readonly lastError: Error | null;
  /** Refresh the incident list (dispatch security.incidents.list). */
  refresh(): Promise<void>;
}

/** True iff a stream event is a security scan result. */
export function isScanEvent(e: DeploymentStreamEvent): boolean {
  return e.origin === "security" && e.code === "security.scan";
}

/** Parse a security.scan stream event into a ThreatEntry. */
export function toThreatEntry(e: DeploymentStreamEvent): ThreatEntry {
  const d = (typeof e.detail === "object" && e.detail !== null ? e.detail : {}) as Record<string, unknown>;
  return {
    atMs: e.atMs,
    label: typeof d["label"] === "string" ? d["label"] : "",
    findingsCount: typeof d["findingsCount"] === "number" ? d["findingsCount"] : 0,
    riskScore: typeof d["riskScore"] === "number" ? d["riskScore"] : 0,
    status: d["status"] === "failure" ? "failure" : "success",
  };
}

/** Parse a security.debt.summary dispatch result. */
export function toDebtSummary(raw: unknown): DebtSummary | null {
  if (typeof raw !== "object" || raw === null) return null;
  const x = raw as Record<string, unknown>;
  return {
    totalScore: typeof x["totalScore"] === "number" ? x["totalScore"] : 0,
    entryCount: typeof x["entryCount"] === "number" ? x["entryCount"] : 0,
  };
}

/** Parse one incident row from a security.incidents.list dispatch result. */
export function toIncidentEntry(raw: unknown): IncidentEntry {
  const x = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    id: typeof x["id"] === "string" ? x["id"] : "",
    title: typeof x["title"] === "string" ? x["title"] : "",
    type: typeof x["type"] === "string" ? x["type"] : "",
    severity: typeof x["severity"] === "string" ? x["severity"] : "",
    status: typeof x["status"] === "string" ? x["status"] : "",
    detectedAt: typeof x["detectedAt"] === "number" ? x["detectedAt"] : 0,
  };
}
